import { useState } from 'react';
import {
  Table,
  Select,
  Modal,
  SurfaceCard,
  PageContainer,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type {
  SalesDelivery,
  SalesDeliveryStatus,
} from '../../domain/entities/delivery.entity';
import { useSalesDeliveries } from '../hooks/use-sales-deliveries';
import {
  DeliveryStatusBadge,
  DELIVERY_STATUS_OPTIONS,
  formatDateTime,
  Field,
  Mono,
} from '../shared/display';
import { RefreshButton } from '@/shared/RefreshButton';
import { EntityLink } from '@/shared/components/entity-link';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...DELIVERY_STATUS_OPTIONS,
];

const columns: ColumnDef<SalesDelivery>[] = [
  {
    key: 'productExternalId',
    header: 'Produto (gateway)',
    render: (row) => <Mono>{row.productExternalId}</Mono>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <DeliveryStatusBadge status={row.status} />,
  },
  {
    key: 'userId',
    header: 'Usuário',
    render: (row) => (
      <Mono>
        <EntityLink kind="user" id={row.userId} />
      </Mono>
    ),
  },
  {
    key: 'attemptCount',
    header: 'Tentativas',
    render: (row) => (
      <span className="text-(--color-text-secondary)">{row.attemptCount}</span>
    ),
  },
  {
    key: 'updatedAt',
    header: 'Atualizada em',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {formatDateTime(row.updatedAt)}
      </span>
    ),
  },
];

export function DeliveriesPage() {
  const [page, setPage] = usePageQueryParam();
  const [status, setStatus] = useState<SalesDeliveryStatus | ''>('');
  const [selected, setSelected] = useState<SalesDelivery | null>(null);

  const { data, isLoading, refetch, isFetching } = useSalesDeliveries(
    page,
    PER_PAGE,
    status || undefined,
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  const handleStatusChange = (value: string) => {
    setStatus(value as SalesDeliveryStatus | '');
    setPage(1);
  };

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Entregas
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Fila de entregas de vendas (SalesDelivery), das mais recentes para
            as mais antigas. Clique em uma entrega para ver os detalhes.
          </p>
        </div>
        <RefreshButton
          onRefresh={() => void refetch()}
          isRefreshing={isFetching}
        />
      </div>

      <SurfaceCard className="space-y-5">
        <div className="max-w-xs">
          <Select
            label="Filtrar por status"
            id="delivery-status-filter"
            options={STATUS_FILTER_OPTIONS}
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
          />
        </div>

        <Table
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="Nenhuma entrega encontrada."
          striped
          onRowClick={(row) => setSelected(row)}
        />

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total}{' '}
                {data.total === 1
                  ? 'entrega encontrada'
                  : 'entregas encontradas'}
              </>
            ) : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </SurfaceCard>

      <DeliveryDetailModal
        delivery={selected}
        onClose={() => setSelected(null)}
      />
    </PageContainer>
  );
}

function DeliveryDetailModal({
  delivery,
  onClose,
}: {
  delivery: SalesDelivery | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={delivery !== null}
      onClose={onClose}
      title="Detalhe da entrega"
      description={delivery ? `ID ${delivery.id}` : undefined}
      size="lg"
    >
      {delivery && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Status"
              value={<DeliveryStatusBadge status={delivery.status} />}
            />
            <Field label="Tentativas" value={delivery.attemptCount} />
            <Field
              label="Produto (gateway)"
              value={<Mono>{delivery.productExternalId}</Mono>}
            />
            <Field
              label="Usuário"
              value={
                <Mono>
                  <EntityLink kind="user" id={delivery.userId} />
                </Mono>
              }
            />
            <Field
              label="Contrato"
              value={
                <Mono>
                  <EntityLink kind="contract" id={delivery.contractId} />
                </Mono>
              }
            />
            <Field
              label="Transação (origem)"
              value={
                <Mono>
                  <EntityLink
                    kind="transaction"
                    id={delivery.sourceInfo.transactionId}
                  />
                </Mono>
              }
            />
            <Field
              label="Nesse status desde"
              value={formatDateTime(delivery.inCurrentStatusSince)}
            />
            <Field
              label="Última tentativa"
              value={formatDateTime(delivery.lastAttemptAt)}
            />
            <Field
              label="Criada em"
              value={formatDateTime(delivery.createdAt)}
            />
            <Field
              label="Atualizada em"
              value={formatDateTime(delivery.updatedAt)}
            />
          </div>

          <Field label="Motivo do status" value={delivery.statusReason} />
          <Field
            label="Mensagem de erro"
            value={
              delivery.errorMessage ? (
                <span className="text-red-600 dark:text-red-400">
                  {delivery.errorMessage}
                </span>
              ) : null
            }
          />
          <Field
            label="Perks concedidos"
            value={
              delivery.grantedPerkIds.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {delivery.grantedPerkIds.map((id) => (
                    <span
                      key={id}
                      className="border-(--color-border) bg-(--color-bg-secondary) rounded-md border px-2 py-0.5 font-mono text-xs"
                    >
                      {id}
                    </span>
                  ))}
                </div>
              ) : null
            }
          />
        </div>
      )}
    </Modal>
  );
}
